import { useRef, useState, useEffect, useCallback } from 'react';
import { computeOrientedImageStyle } from './cameraOrientation.js';

// Shared by any card that shows a single img/video and needs the
// rotate-to-portrait treatment from cameraOrientation.js. Call
// `captureNaturalSize` from the media's onLoad/onLoadedMetadata, attach
// `containerRef` to the box the media should fit inside, and apply
// `display.style` (and `display.isVertical` for the black background).
//
// `containerRef` is a callback ref, not a plain useRef object: the natural
// size is often known (from a preloaded/cached image) before the real
// container ever mounts — e.g. while a caller is still showing its own
// loading placeholder on a different <div>. A plain ref wouldn't re-trigger
// the size effect once that placeholder is swapped for the real container,
// leaving `display` stuck at null (so the media renders unrotated) until
// something else happens to change. A callback ref fires on every mount, so
// `containerNode` — and this effect — react correctly when the box appears.
export function useOrientedMedia(rotationDeg) {
  const nodeRef = useRef(null);
  const [containerNode, setContainerNode] = useState(null);
  const containerRef = useCallback((node) => {
    nodeRef.current = node;
    setContainerNode(node);
  }, []);

  const [naturalDims, setNaturalDims] = useState(null);
  const [display, setDisplay] = useState(null);

  const captureNaturalSize = useCallback((el) => {
    const width = el?.naturalWidth || el?.videoWidth;
    const height = el?.naturalHeight || el?.videoHeight;
    if (width && height) setNaturalDims({ width, height });
  }, []);

  useEffect(() => {
    if (!naturalDims || !containerNode) return;
    const rect = containerNode.getBoundingClientRect();
    setDisplay(computeOrientedImageStyle({
      rotationDeg,
      naturalWidth: naturalDims.width,
      naturalHeight: naturalDims.height,
      containerWidth: rect.width,
      containerHeight: rect.height,
    }));
  }, [naturalDims, rotationDeg, containerNode]);

  // For imperative reads (e.g. a drag handler computing a position from the
  // container's current rect) that don't need to be reactive.
  return { containerRef, nodeRef, captureNaturalSize, display };
}
